/**
 * Guard para que el evento `click` (pointerup) no pise la selección hecha en
 * pointerDown dentro del mismo gesto.
 *
 * Problema: al seleccionar en pointerDown, el re-render de la selección
 * (PivotControls, overrides de raycast) puede sacar sus meshes del raycast del
 * `click`. Entonces:
 *  - si el rayo no encuentra nada -> onPointerMissed del Canvas -> unselectAll()
 *  - si el rayo encuentra OTRO item -> su handleClick lo selecciona pisando al original.
 */
const GUARD_TTL_MS = 1500

let gestureSelection: { id: string; at: number } | null = null

/**
 * Tras un arrastre real, el `click` que el navegador dispara al soltar puede
 * caer sobre OTRA unidad (la arrastrada va con retraso respecto al puntero) y
 * seleccionarla. Se marca el gesto como "arrastre" y todos los handleClick lo
 * ignoran hasta el siguiente pointerdown.
 */
let dragGestureSuppressingClicks = false

// Limpiar el guard al inicio de cada gesto (fase captura: corre antes que r3f).
if (typeof window !== 'undefined') {
  window.addEventListener(
    'pointerdown',
    () => {
      gestureSelection = null
      dragGestureSuppressingClicks = false
    },
    true,
  )
}

/** Llamar al terminar un gesto que tuvo arrastre real. */
export const markDragGestureClickSuppression = () => {
  dragGestureSuppressingClicks = true
}

/** ¿El click/pointerMissed actual pertenece a un gesto de arrastre? */
export const shouldSuppressClickAfterDrag = () => dragGestureSuppressingClicks

/** Llamar cuando una selección se aplique durante un pointerDown. */
export const markPointerDownSelection = (id: string) => {
  gestureSelection = { id: String(id), at: Date.now() }
}

const isGuardActive = () =>
  gestureSelection != null && Date.now() - gestureSelection.at <= GUARD_TTL_MS

/**
 * Id seleccionado en el pointerDown del gesto actual (o null si no hubo).
 * No consume el guard: pueden consultarlo varios handlers del mismo click.
 */
export const getActivePointerDownSelectionId = (): string | null =>
  isGuardActive() ? gestureSelection!.id : null

/**
 * Llamar desde el onPointerMissed del Canvas. Devuelve true si el "miss"
 * pertenece al mismo gesto que una selección en pointerDown y debe ignorarse.
 * Consume el guard.
 */
export const consumePointerMissedSuppression = () => {
  const shouldSuppress = isGuardActive()
  gestureSelection = null
  return shouldSuppress
}
